import React from 'react';
import { useTranslation } from 'react-i18next';
import { Formik, Form, Field } from 'formik';
import { toFormikValidationSchema } from 'zod-formik-adapter';
import Grid from 'apollo-react/components/Grid';
import PasswordField from '../../Form/passwordField';
import ButtonCommon from '../../Button';
import {
  verifyMyPasswordSchema,
  changeMyPasswordSchema,
} from '../../../../validation/user';
import styles from '../Profile.module.css';

const PasswordSection = ({
  verified,
  requireCurrentPassword,
  onVerifyPassword,
  onChangePassword,
}) => {
  const { t } = useTranslation();
  const passwordSchema = toFormikValidationSchema(
    changeMyPasswordSchema(t, requireCurrentPassword)
  );

  const handleVerifyClick = (values, setFieldError) => {
    const result = verifyMyPasswordSchema(t).safeParse({
      currentPassword: values.currentPassword,
    });
    if (!result.success) {
      setFieldError('currentPassword', result.error.issues[0].message);
      return;
    }
    onVerifyPassword(values.currentPassword);
  };

  return (
    <Formik
      initialValues={{
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      }}
      validationSchema={passwordSchema}
      onSubmit={async (values) => {
        await onChangePassword(values.currentPassword, values.newPassword);
      }}
    >
      {({ values, setFieldValue, handleSubmit, setFieldError }) => (
        <Form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {requireCurrentPassword && (
              <Grid size={12}>
                <div className={styles.flexRowAlign}>
                  <div className={styles.flex1}>
                    <Field name="currentPassword">
                      {({ field, meta }) => (
                        <PasswordField
                          field={field}
                          meta={meta}
                          label={t('profileData.currentPassword')}
                          value={values.currentPassword}
                          onChange={(v) => setFieldValue('currentPassword', v)}
                        />
                      )}
                    </Field>
                  </div>
                  <ButtonCommon
                    color="primary"
                    className={`${styles.btnSized} ${styles.mt29}`}
                    onClick={() => handleVerifyClick(values, setFieldError)}
                    text={t('profileData.verify')}
                  />
                </div>
              </Grid>
            )}
            <Grid size={{ xs: 6, sm: 6 }}>
              <div
                className={
                  requireCurrentPassword && !verified ? styles.opacityDim : ''
                }
              >
                <Field name="newPassword">
                  {({ field, meta }) => (
                    <PasswordField
                      field={field}
                      meta={meta}
                      label={t('profileData.newPassword')}
                      value={values.newPassword}
                      onChange={(v) => setFieldValue('newPassword', v)}
                      disabled={requireCurrentPassword ? !verified : false}
                    />
                  )}
                </Field>
              </div>
            </Grid>
            <Grid size={{ xs: 6, sm: 6 }}>
              <div
                className={
                  requireCurrentPassword && !verified ? styles.opacityDim : ''
                }
              >
                <Field name="confirmPassword">
                  {({ field, meta }) => (
                    <PasswordField
                      field={field}
                      meta={meta}
                      label={t('profileData.confirmPassword')}
                      value={values.confirmPassword}
                      onChange={(v) => setFieldValue('confirmPassword', v)}
                      disabled={requireCurrentPassword ? !verified : false}
                    />
                  )}
                </Field>
              </div>
            </Grid>
            <Grid size={12}>
              <div className={styles.actionsRightNoMargin}>
                <ButtonCommon
                  color="primary"
                  className={styles.btnSized}
                  type="submit"
                  disabled={
                    requireCurrentPassword
                      ? !verified ||
                        !values.newPassword ||
                        !values.confirmPassword
                      : !values.newPassword || !values.confirmPassword
                  }
                  text={t('Save')}
                />
              </div>
            </Grid>
          </Grid>
        </Form>
      )}
    </Formik>
  );
};

export default PasswordSection;
